import type { User, Permission } from '@/types';
import { GuideStatus } from '@/types';
import {
  getGuideStatus,
  hasActiveGuideApplication,
  hasGuideProfile,
  isApprovedGuide,
  isGuidePendingReview,
  needsOnboarding,
} from '@/features/auth/utils/resolvePostAuthRoute';
import type { CampaignEligibility, GuideCampaignConfig } from '../config/types';
import { isCampaignActive } from '../config/resolveCampaignContent';
import { shouldForceGuideRecruitmentCampaign } from '../config/guide-recruitment.campaign';
import {
  hasCampaignShownThisSession,
  hasEnteredGuideFlowThisSession,
  isCampaignDismissed,
} from './campaignDismissal';

function isEmptyDraft(user: User | null): boolean {
  if (!hasGuideProfile(user)) return false;
  const status = getGuideStatus(user);
  const step = user?.guideProfile?.onboardingStep ?? user?.onboardingStep ?? 0;
  return status === GuideStatus.DRAFT && step === 0;
}

export function resolveCampaignEligibility(
  config: GuideCampaignConfig,
  options: {
    user: User | null;
    permissions: Permission[];
    isInitializing: boolean;
    isLandingPage: boolean;
    authModalOpen: boolean;
    userId?: string;
  },
): CampaignEligibility {
  if (!options.isLandingPage) {
    return { show: false, reason: 'not_landing' };
  }

  if (!isCampaignActive(config)) {
    return { show: false, reason: 'campaign_inactive' };
  }

  if (options.isInitializing) {
    return { show: false, reason: 'auth_initializing' };
  }

  if (options.authModalOpen) {
    return { show: false, reason: 'auth_modal_open' };
  }

  if (hasEnteredGuideFlowThisSession()) {
    return { show: false, reason: 'guide_flow_entered' };
  }

  if (hasCampaignShownThisSession()) {
    return { show: false, reason: 'shown_this_session' };
  }

  if (
    isCampaignDismissed(config.id, config.dismissalCooldownDays, options.userId)
  ) {
    return { show: false, reason: 'dismissed' };
  }

  const { user, permissions } = options;

  if (isApprovedGuide(user, permissions) && !shouldForceGuideRecruitmentCampaign()) {
    return { show: false, reason: 'approved_guide' };
  }

  if (isGuidePendingReview(user) && !shouldForceGuideRecruitmentCampaign()) {
    return { show: false, reason: 'pending_review' };
  }

  if (hasActiveGuideApplication(user) && !shouldForceGuideRecruitmentCampaign()) {
    return { show: false, reason: 'active_application' };
  }

  if (hasGuideProfile(user) && needsOnboarding(user) && !isEmptyDraft(user) && !shouldForceGuideRecruitmentCampaign()) {
    return { show: false, reason: 'in_onboarding' };
  }

  if (isEmptyDraft(user)) {
    return { show: true, variant: 'continue' };
  }

  if (hasGuideProfile(user)) {
    return { show: false, reason: 'has_guide_profile' };
  }

  return { show: true, variant: 'recruit' };
}
